import React, { useEffect, useMemo, useState } from "react";
import "./WealthSeriesPortfolioPerformanceGraph.scss";
import SparkLines from "../../../common/SparkLines";
import { dummyOptions } from "../../../TradingView/helpers";
import { connect } from "react-redux";
import {
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { portfolioPerformanceLoad } from "../../../../appRedux/ducks/wealthPerformance";
import { multiSparklineDataLoad } from "../../../../appRedux/ducks/multiSparklineData";
import moment from "moment";
import { useParams } from "react-router-dom";
import { init_portfolio } from "../../../../appRedux/ducks/wealthPortfolio";
import { urlToNameMap } from "../../../WealthSeriesOverview/portfoliosData";
import { apiURL } from "../../../../appRedux/actions/helpers";

// Rebase benchmark closes to % change from their first value and ratio-map
// them onto the portfolio series' index domain so the overlays align.
const normalizeBenchmarkSeries = (closes, portfolioPoints) => {
  const normalized = [];
  const firstValue = parseFloat(closes[0]);
  if (isNaN(firstValue) || firstValue <= 0) return normalized;
  for (let i = 0; i < portfolioPoints.length; i++) {
    const closeIndex = Math.min(
      Math.floor(i * (closes.length / portfolioPoints.length)),
      closes.length - 1,
    );
    const value = parseFloat(closes[closeIndex]);
    if (!isNaN(value) && value > 0) {
      normalized.push({
        x: i,
        y: parseFloat((((value - firstValue) / firstValue) * 100).toFixed(2)),
        date: portfolioPoints[i]?.date,
      });
    }
  }
  return normalized;
};

const WealthSeriesPortfolioPerformanceGraph = ({
  loadSparkline,
  sparklineLoading,
  sparklineData,
  loadedPeriod,
  wealthPortfolios,
  loadingPortfolio,
  tabValue,
  loadSpyData,
  spyData,
  spyLoading,
  spyTimeframe,
  timeframe,
  setTimeframe,
  portfolioData,
  simulatedCutoff,
  enableBtcBenchmark = false,
  btcData,
  btcLoading,
  btcTimeframe,
}) => {
  const { portfolio } = useParams();
  const { series: _series } = useParams();
  const [resolution, setResolution] = useState(timeframe || "1y");
  const [showSPY, setShowSPY] = useState(true);
  const [showBTC, setShowBTC] = useState(true);
  const [wowMomPerformance, setWowMomPerformance] = useState(null);
  // Series-derived value per visited period, so each period button shows the
  // same figure as the header/chart for that period instead of the backend's
  // separately-precomputed portfolio.performance entry (they can disagree).
  const [seriesPerformanceByPeriod, setSeriesPerformanceByPeriod] = useState(
    {},
  );

  useEffect(() => {
    if (timeframe !== resolution) {
      setResolution(timeframe);
    }
  }, [timeframe]);

  const handleResolutionChange = (_, newResolution) => {
    if (newResolution) {
      setResolution(newResolution);
      if (setTimeframe) {
        setTimeframe(newResolution);
      }
    }
  };
  const handleSPYToggle = (event) => {
    setShowSPY(event.target.checked);
  };
  const handleBTCToggle = (event) => {
    setShowBTC(event.target.checked);
  };

  // Use portfolioData from props if available, otherwise fall back to wealthPortfolios[portfolio]
  const wealthPortfolio =
    portfolioData || wealthPortfolios[portfolio] || init_portfolio;

  const scalingFactor = 100; // Adjust this factor as needed

  const memoizedSparklineData = useMemo(() => {
    return (
      sparklineData?.map((el, i) => {
        const timestamp = moment(el.created_at).valueOf();
        return {
          x: i,
          date: timestamp,
          y:
            tabValue === 0
              ? (Number(el.twr_performance) * scalingFactor).toFixed(2)
              : (Number(el.total_growth) * scalingFactor).toFixed(2),
        };
      }) || []
    );
  }, [sparklineData, tabValue]);

  // Calculate overall performance for the current timeframe
  const overallPerformance = useMemo(() => {
    if (sparklineData?.length > 0) {
      const lastPoint = sparklineData[sparklineData.length - 1];
      const performance =
        tabValue === 0
          ? Number(lastPoint.twr_performance) * scalingFactor
          : Number(lastPoint.total_growth) * scalingFactor;
      return performance.toFixed(2);
    }
    return "0.00";
  }, [sparklineData, tabValue, scalingFactor]);

  // Get timeframe display text
  const timeframeText = useMemo(() => {
    if (sparklineData?.length > 0) {
      const startDate = moment(sparklineData[0].created_at).format("MMM YYYY");
      return `${startDate} – Today`;
    }
    return "";
  }, [sparklineData]);

  // Format date labels based on timeframe
  const dateLabels = useMemo(() => {
    if (!sparklineData?.length) return { startDate: "", endDate: "" };

    const isShortTimeframe =
      resolution === "1d" || resolution === "7d" || resolution === "1m";
    const format = isShortTimeframe ? "MMM DD, YYYY" : "MMM YYYY";

    const startDate = moment(sparklineData[0].created_at).format(format);
    const endDate = moment(
      sparklineData[sparklineData.length - 1].created_at,
    ).format(format);

    return { startDate, endDate };
  }, [sparklineData, resolution]);

  const updatedOptions = useMemo(() => {
    if (sparklineData?.length) {
      const isUpwardTrend =
        sparklineData.length > 1 &&
        sparklineData[0].twr_performance <
          sparklineData[sparklineData.length - 1].twr_performance;
      // simulatedCutoff overrides the per-series default; null disables the
      // "Simulated data" band entirely (e.g. Bitcoin Navigator is live-only).
      const cutoffDateMs =
        simulatedCutoff !== undefined
          ? simulatedCutoff === null
            ? null
            : moment(simulatedCutoff).valueOf()
          : _series === "core"
            ? moment("2025-09-25").valueOf()
            : moment("2024-12-23").valueOf();
      let cutoffIndex =
        cutoffDateMs === null
          ? 0
          : memoizedSparklineData.findIndex(
              (point) => point.date > cutoffDateMs,
            );
      cutoffIndex =
        cutoffIndex === -1 ? memoizedSparklineData.length - 1 : cutoffIndex - 1;
      const annotations = {
        xaxis:
          cutoffIndex > 0
            ? [
                {
                  x: 0,
                  x2: memoizedSparklineData[cutoffIndex].x,
                  fillColor: "#ECECEC",
                  opacity: 0.4,
                  label: {
                    borderColor: "#ECECEC",
                    style: {
                      color: "#333",
                      background: "#ECECEC",
                    },
                    text: "Simulated data",
                  },
                },
              ]
            : [],
      };

      // Setup series data; each overlay pushes its color in lockstep with its
      // series so toggling any subset never shifts colors between lines.
      const series = [
        {
          name: "Portfolio",
          data: memoizedSparklineData,
        },
      ];
      const seriesColors = [
        tabValue === 1 ? "#1976d2" : isUpwardTrend ? "#5EEECD" : "#F32228",
      ];

      if (
        enableBtcBenchmark &&
        showBTC &&
        btcData?.length > 0 &&
        memoizedSparklineData.length > 0 &&
        btcTimeframe === resolution.toUpperCase()
      ) {
        const btcNormalizedData = normalizeBenchmarkSeries(
          btcData,
          memoizedSparklineData,
        );
        if (btcNormalizedData.length > 0) {
          series.push({
            name: "BTC",
            data: btcNormalizedData,
          });
          seriesColors.push("#F7931A");
        }
      }

      if (
        showSPY &&
        spyData?.length > 0 &&
        memoizedSparklineData.length > 0 &&
        spyTimeframe === resolution.toUpperCase()
      ) {
        const spyNormalizedData = normalizeBenchmarkSeries(
          spyData,
          memoizedSparklineData,
        );
        if (spyNormalizedData.length > 0) {
          series.push({
            name: "S&P 500",
            data: spyNormalizedData,
          });
          seriesColors.push("#1976d2");
        }
      }

      return {
        ...dummyOptions,
        tooltip: {
          ...dummyOptions.tooltip,
          shared: true,
          x: {
            formatter: (_, { dataPointIndex }) => {
              const date = sparklineData[dataPointIndex]?.created_at;
              return date
                ? moment.tz(date, "America/New_York").format("MMM DD, YYYY")
                : "";
            },
          },
          y: {
            formatter: (val, { seriesIndex, dataPointIndex }) => {
              if (seriesIndex === 0) {
                const currentValue =
                  (
                    sparklineData[dataPointIndex]?.twr_performance * 100
                  ).toFixed(2) + "%";
                return `${currentValue}`;
              } else {
                return `${val}%`;
              }
            },
          },
        },
        colors: seriesColors,
        stroke: {
          width: 2,
          curve: "smooth",
        },
        annotations,
        series,
      };
    }
    return dummyOptions;
  }, [
    sparklineData,
    tabValue,
    memoizedSparklineData,
    showSPY,
    spyData,
    spyTimeframe,
    enableBtcBenchmark,
    showBTC,
    btcData,
    btcTimeframe,
    resolution,
    _series,
    simulatedCutoff,
  ]);

  // Backend returns { totalRecords: 0, records: [] } when the requested
  // period predates the portfolio's history — render an honest empty state
  // instead of a blank chart and a fake +0.00%.
  const hasSeriesData = memoizedSparklineData.length > 0;

  // WoW/MoM is intentionally its own metric (week-over-week / month-over-month
  // from the dedicated ?period=wow|mom endpoint), distinct from the rolling
  // 7D/1M window return shown in the header — the values are expected to
  // differ. The endpoint returns { basis, totalRecords: 0, records: [] } when
  // there isn't enough history for the comparison, and the line hides rather
  // than falling back to the rolling-window figure (a different metric).
  const renderInfo = (resolution) => {
    if (resolution !== "1d" && resolution !== "1y" && resolution !== "ytd") {
      if (!hasSeriesData || wowMomPerformance === null) return null;
      const label =
        resolution === "7d" ? "Week-on-week (WoW): " : "Month-on-month (MoM): ";
      const displayValue = Number((wowMomPerformance * 100).toFixed(2));
      if (isNaN(displayValue)) return null;
      return label + (displayValue >= 0 ? "+" : "") + displayValue + "%";
    }
    return null;
  };

  useEffect(() => {
    if (wealthPortfolio.portfolio_id && resolution) {
      loadSparkline(wealthPortfolio.portfolio_id, resolution);
    }
  }, [resolution, wealthPortfolio.portfolio_id, loadSparkline]);

  // Cache the loaded series' final values under the period they were fetched
  // for (loadedPeriod is set by the duck alongside the data, so a fast period
  // switch can't attribute a stale series to the wrong button).
  useEffect(() => {
    if (!sparklineLoading && loadedPeriod && sparklineData?.length > 0) {
      const lastPoint = sparklineData[sparklineData.length - 1];
      setSeriesPerformanceByPeriod((prev) => ({
        ...prev,
        [loadedPeriod]: {
          twr: Number(lastPoint.twr_performance),
          growth: Number(lastPoint.total_growth),
        },
      }));
    }
  }, [sparklineData, sparklineLoading, loadedPeriod]);

  useEffect(() => {
    setSeriesPerformanceByPeriod({});
  }, [wealthPortfolio.portfolio_id]);

  // Seed every period button from its own series on load (TRAD-1274): the
  // backend's precomputed portfolio.performance entries can disagree with the
  // freshly computed series, so without this only clicked periods showed the
  // corrected value. One small fetch per period, cached by portfolio.
  useEffect(() => {
    const portfolioId = wealthPortfolio.portfolio_id;
    if (!portfolioId) return undefined;
    let cancelled = false;

    const seedPeriods = async () => {
      const entries = await Promise.all(
        ["1d", "7d", "1m", "1y", "ytd"].map(async (period) => {
          try {
            const response = await fetch(
              `${apiURL}/wealth-series/portfolios/${portfolioId}/performance?period=${period}`,
              { credentials: "include" },
            );
            const data = await response.json();
            const records = data.performance?.records;
            if (!records?.length) return null;
            const lastPoint = records[records.length - 1];
            return [
              period,
              {
                twr: Number(lastPoint.twr_performance),
                growth: Number(lastPoint.total_growth),
              },
            ];
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;
      const seeded = entries.filter(Boolean);
      if (seeded.length) {
        setSeriesPerformanceByPeriod((prev) => ({
          ...prev,
          ...Object.fromEntries(seeded),
        }));
      }
    };

    seedPeriods();
    return () => {
      cancelled = true;
    };
  }, [wealthPortfolio.portfolio_id]);

  useEffect(() => {
    if (showSPY) {
      loadSpyData(["SPY"], "1", true, resolution.toUpperCase());
    }
  }, [showSPY, loadSpyData, resolution]);

  // Separate effect so flipping one benchmark toggle never force-refetches
  // the other symbol (the duck keys data/loading/timeframe per symbol).
  useEffect(() => {
    if (enableBtcBenchmark && showBTC) {
      loadSpyData(["IBIT"], "1", true, resolution.toUpperCase());
    }
  }, [enableBtcBenchmark, showBTC, loadSpyData, resolution]);

  // Fetch WoW/MoM data from dedicated endpoints
  useEffect(() => {
    if (
      wealthPortfolio.portfolio_id &&
      (resolution === "7d" || resolution === "1m")
    ) {
      const periodType = resolution === "7d" ? "wow" : "mom";
      const fetchWowMomData = async () => {
        try {
          const response = await fetch(
            `${apiURL}/wealth-series/portfolios/${wealthPortfolio.portfolio_id}/performance?period=${periodType}`,
            { credentials: "include" },
          );
          const data = await response.json();

          if (data.performance?.records?.[0]?.twr_performance !== undefined) {
            setWowMomPerformance(data.performance.records[0].twr_performance);
          } else {
            setWowMomPerformance(null);
          }
        } catch (error) {
          console.error("Failed to fetch WoW/MoM data:", error);
          setWowMomPerformance(null);
        }
      };

      fetchWowMomData();
    } else {
      setWowMomPerformance(null);
    }
  }, [resolution, wealthPortfolio.portfolio_id]);

  const isPositivePerformance = parseFloat(overallPerformance) >= 0;

  return (
    <>
      {/* Performance header with S&P500 toggle */}
      <div className="ws-portfolio-performance-header">
        <div className="ws-portfolio-performance-summary">
          <div className="ws-portfolio-performance-value">
            {hasSeriesData || sparklineLoading ? (
              <span className={isPositivePerformance ? "positive" : "negative"}>
                {isPositivePerformance && "+"}
                {overallPerformance}%
              </span>
            ) : (
              <span>—</span>
            )}
          </div>
          <div className="ws-portfolio-performance-timeframe">
            {timeframeText}
          </div>
        </div>

        <div className="ws-portfolio-spy-toggle">
          {enableBtcBenchmark && (
            <FormControlLabel
              control={
                <Switch
                  checked={showBTC}
                  onChange={handleBTCToggle}
                  color="primary"
                />
              }
              labelPlacement="start"
              label={
                <div className="spy-toggle-label">
                  <span
                    className={`spy-color-dot btc-color-dot ${showBTC ? "active" : ""}`}
                  ></span>
                  <span>BTC</span>
                </div>
              }
            />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={showSPY}
                onChange={handleSPYToggle}
                color="primary"
              />
            }
            labelPlacement="start"
            label={
              <div className="spy-toggle-label">
                <span
                  className={`spy-color-dot ${showSPY ? "active" : ""}`}
                ></span>
                <span>S&P500</span>
              </div>
            }
          />
        </div>
      </div>

      <div className="ws-portfolio-performance-graph">
        {sparklineLoading ||
        (showSPY && spyLoading) ||
        (enableBtcBenchmark && showBTC && btcLoading) ? (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        ) : !hasSeriesData ? (
          <div className="ws-portfolio-performance-empty">
            Not enough history for this period yet.
          </div>
        ) : (
          <>
            <SparkLines
              series={
                updatedOptions.series || [
                  {
                    name: urlToNameMap[portfolio],
                    data: memoizedSparklineData,
                  },
                ]
              }
              options={updatedOptions}
              type={updatedOptions.chart.type}
              height={updatedOptions.chart.height}
              width={updatedOptions.chart.width}
              className="w-full"
            />

            {/* Date labels */}
            <div className="ws-portfolio-chart-date-labels">
              <span className="start-date">{dateLabels.startDate}</span>
              <span className="end-date">{dateLabels.endDate}</span>
            </div>
          </>
        )}

        <div className="ws-portfolio-performance-graph-inform">
          {renderInfo(resolution)}
        </div>
        {_series === "core" && (
          <div className="ws-portfolio-performance-graph-inform mb-0">
            Data before Sep 25, 2025 is simulated
          </div>
        )}
        <ToggleButtonGroup
          value={resolution}
          exclusive
          onChange={handleResolutionChange}
          aria-label="Graph Period"
          className="ws-portfolio-resolutions ws-portfolio-performance-toggles"
          disabled={loadingPortfolio}
        >
          {(loadingPortfolio
            ? [
                { period: "1d" },
                { period: "7d" },
                { period: "1m" },
                { period: "1y" },
                { period: "ytd" },
              ]
            : wealthPortfolio?.performance || []
          )
            ?.filter(
              (data) =>
                data?.period?.toUpperCase() !== "ITD" &&
                data?.period?.toUpperCase() !== "1H" &&
                ["1D", "7D", "1M", "1Y", "YTD"].includes(
                  data?.period?.toUpperCase(),
                ),
            )
            ?.map((data, index) => {
              const periodValue = data?.period?.toLowerCase();
              // Prefer the value derived from that period's loaded series so
              // the selected button always matches the header/chart; fall
              // back to the backend's precomputed entry until visited.
              const cached = seriesPerformanceByPeriod[periodValue];
              const cachedValue = cached
                ? tabValue === 1
                  ? cached.growth
                  : cached.twr
                : undefined;
              const performanceValue =
                cachedValue !== undefined && !isNaN(cachedValue)
                  ? cachedValue
                  : data?.performance;
              const displayValue = Number((performanceValue * 100).toFixed(2));
              const isPositive = displayValue >= 0;

              return (
                <ToggleButton
                  key={"performance_toggle_" + (periodValue || index)}
                  value={periodValue}
                  className="ws-portfolio-resolution ws-portfolio-info-performance-toggle"
                  disabled={loadingPortfolio}
                >
                  {loadingPortfolio ? (
                    <>
                      <div className="ws-portfolio-info-performance-label">
                        {data.period.toUpperCase()}
                      </div>
                      <div className="ws-portfolio-info-performance-value">
                        --.--%
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ws-portfolio-info-performance-label">
                        {data?.period?.toUpperCase()}
                      </div>
                      <div
                        className={`ws-portfolio-info-performance-value ${isPositive ? "positive" : "negative"}`}
                      >
                        {isPositive && "+"}
                        {displayValue === 0 ? "0.00" : displayValue.toFixed(2)}%
                      </div>
                    </>
                  )}
                </ToggleButton>
              );
            })}
        </ToggleButtonGroup>
      </div>
    </>
  );
};

const stateToProps = (state) => ({
  sparklineData: state.wealthPerformance.sparklineData,
  sparklineLoading: state.wealthPerformance.loading,
  loadedPeriod: state.wealthPerformance.loadedPeriod,
  wealthPortfolios: state.wealthPortfolio,
  spyData: state.multiSparklineData.sparklineData.SPY || [],
  spyLoading: state.multiSparklineData.loading.SPY || false,
  spyTimeframe: state.multiSparklineData.timeframes?.SPY || null,
  btcData: state.multiSparklineData.sparklineData.IBIT || [],
  btcLoading: state.multiSparklineData.loading.IBIT || false,
  btcTimeframe: state.multiSparklineData.timeframes?.IBIT || null,
});

const dispatchToProps = (dispatch) => ({
  loadSparkline: (portfolioId, resolution) =>
    dispatch(portfolioPerformanceLoad(portfolioId, resolution)),
  loadSpyData: (symbols, resolution, forceRefresh, timeframe) =>
    dispatch(
      multiSparklineDataLoad(
        symbols,
        resolution,
        forceRefresh,
        timeframe || resolution.toUpperCase(),
      ),
    ),
});

export default connect(
  stateToProps,
  dispatchToProps,
)(WealthSeriesPortfolioPerformanceGraph);
